import React, { createContext, useState, useEffect, useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import { protectedResources } from '../authConfig';
import { isTestEnv } from '../globals';
import { useUserCache } from '../hooks/useUserCache';
import { enrichUserData, buildUserData } from '../services/userEnrichmentService';
import {
    HTTP_METHOD_GET,
    ENDPOINT_GET_ACCESS_MODULES,
    FIRST_ACCOUNT_INDEX,
    MODULE_NAMES,
    TOKEN_CLAIMS,
    USER_FIELDS
} from '../constants/userContextConstants';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const { accounts } = useMsal();
  const [user, setUser] = useState({ [USER_FIELDS.MODULES]: undefined });
  const [loading, setLoading] = useState(true);
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });
  const { saveToCache, loadFromCache, clearCache } = useUserCache();

  useEffect(() => {
    const fetchUser = async () => {
      console.log('UserContext: fetchUser called, accounts:', accounts.length);

      if (accounts.length === FIRST_ACCOUNT_INDEX) {
        console.log('UserContext: No accounts - waiting for SSO');
        setLoading(false);
        return;
      }

      try {
        const response = await execute(
          HTTP_METHOD_GET,
          `${process.env.REACT_APP_BACKEND_BASE_URL}${ENDPOINT_GET_ACCESS_MODULES}`
        );

        !isTestEnv && console.log('Response from backend:', response);

        if (response) {
          const { modules, userBranchesList, fullName, email, priorityUserName, roles, _id, hospital } = response;

          const modulesArray = Array.isArray(modules) ? modules : (modules ? [modules] : []);
          const accessRights = modulesArray.reduce((acc, moduleName) => {
            acc[moduleName] = true;
            return acc;
          }, {});

          // 2️⃣ 🆕 קבלת Groups מ-Entra ID
          const account = accounts[0];

          // ✅ בדיקה כפולה שיש account
          if (!account) {
            console.warn('UserContext: account disappeared mid-process!');
            setLoading(false);
            return;
          }

          const userGroups = account.idTokenClaims?.[TOKEN_CLAIMS.GROUPS] || [];
          isTestEnv && console.log('User Groups from Entra ID:', userGroups);
          isTestEnv && console.log('User email:', account.username);

          const adminGroupId = process.env.REACT_APP_ADMIN_GROUP_ID;
          const isAdminByGroup = adminGroupId && userGroups.includes(adminGroupId);
          const isAdminByModule = modules.includes(MODULE_NAMES.ADMIN);
          const isAdmin = isAdminByGroup || isAdminByModule;

          isTestEnv && console.log('Is Admin:', isAdmin, {
            byGroup: isAdminByGroup,
            byModule: isAdminByModule
          });

          const userData = buildUserData(response, account, userGroups, isAdmin);
          const enrichedUserData = enrichUserData(userData, modules, userGroups, isAdmin);
          setUser(enrichedUserData);

          saveToCache(userData, modules, userGroups, isAdmin);
          isTestEnv && console.log('UserContext: userData set successfully');
        }
      } catch (error) {
        isTestEnv && console.error('Failed to fetch user:', error);

        const cached = loadFromCache();
        if (cached) {
          const { userData, modules, userGroups, isAdmin } = cached;
          const enrichedUserData = enrichUserData(userData, modules, userGroups, isAdmin);
          setUser(enrichedUserData);
          isTestEnv && console.log('Loaded user data from cache');
        }
      } finally {
        setLoading(false);
      }
    };

    const cached = loadFromCache();
    if (cached) {
      const { userData, modules, userGroups, isAdmin } = cached;
      const enrichedUserData = enrichUserData(userData, modules, userGroups, isAdmin);
      setUser(enrichedUserData);
      isTestEnv && console.log('Initially loaded user data from cache');
    }

    if (accounts.length > FIRST_ACCOUNT_INDEX) {
      fetchUser();
    } else {
      console.log('UserContext: No accounts yet, waiting...');
      setLoading(false);
    }
  }, [accounts.length, execute, saveToCache, loadFromCache]);

  const value = {
    user,
    setUser,
    loading,
    clearUser: () => {
      clearCache();
      setUser({ [USER_FIELDS.MODULES]: undefined });
    }
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};